import { useRef, useLayoutEffect } from 'react';

interface Snowflake {
  x: number;
  y: number;
  radius: number;
  speed: number;
  wind: number;
  opacity: number;
}

const SnowflakesCanvas = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const snowflakes = useRef<Snowflake[]>([]);

  const initSnowflakes = (width: number, height: number, count = 80) => {
    const flakes: Snowflake[] = [];
    for (let i = 0; i < count; i++) {
      flakes.push({
        x: Math.random() * width,
        y: Math.random() * height,
        radius: Math.random() * 2 + 1,
        speed: Math.random() * 0.5 + 0.2,
        wind: (Math.random() - 0.5) * 0.3,
        opacity: Math.random() * 0.7 + 0.3
      });
    }
    snowflakes.current = flakes;
  };

  const animate = (ctx: CanvasRenderingContext2D, canvas: HTMLCanvasElement) => {
    const width = canvas.width;
    const height = canvas.height;

    ctx.clearRect(0, 0, width, height);

    snowflakes.current.forEach((flake) => {
      flake.y += flake.speed;
      flake.x += flake.wind;

      if (flake.y > height) {
        flake.y = 0;
        flake.x = Math.random() * width;
      }
      if (flake.x > width) flake.x = 0;
      if (flake.x < 0) flake.x = width;

      ctx.beginPath();
      ctx.arc(flake.x, flake.y, flake.radius, 0, Math.PI * 2);
      ctx.fillStyle = `rgba(255, 255, 255, ${flake.opacity})`;
      ctx.fill();
    });

    requestAnimationFrame(() => animate(ctx, canvas));
  };

  useLayoutEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const handleResize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;

      initSnowflakes(canvas.width, canvas.height);
    };

    handleResize();
    window.addEventListener('resize', handleResize);

    animate(ctx, canvas);

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        backgroundColor: 'transparent',
        display: 'block',
        pointerEvents: 'none',
        zIndex: 899
      }}
    />
  );
};

export default SnowflakesCanvas;
